import type { datadogRum as DatadogRum } from "@datadog/browser-rum";
import { CITY } from "./city";

// Module-scoped guard so concurrent callers don't double-init. Set BEFORE the
// dynamic import resolves, not after — otherwise two near-simultaneous calls
// (e.g. requestIdleCallback firing alongside the setTimeout fallback) would
// both pass the check and both call datadogRum.init().
let initialized = false;

// Module-scoped handle to the SDK, set once the dynamic import resolves.
// trackAction() reads this instead of statically importing the SDK — a
// static import here would pull @datadog/browser-rum straight back into
// the entry chunk.
let rumHandle: typeof DatadogRum | undefined;

export async function initRum(): Promise<void> {
  const applicationId = import.meta.env.VITE_DATADOG_APPLICATION_ID as
    | string
    | undefined;
  const clientToken = import.meta.env.VITE_DATADOG_CLIENT_TOKEN as
    | string
    | undefined;

  // No-op when tokens are unset (dev environment or RUM not configured).
  if (!applicationId || !clientToken) {
    return;
  }

  if (initialized) {
    return;
  }
  initialized = true;

  // The chunk is fetched lazily, so this can fail offline (e.g. a PWA
  // reload where the service worker hasn't cached it yet). Swallow and
  // reset the guard so a later call — e.g. after connectivity returns —
  // can retry, matching the retry pattern in store.ts's loadAllStatuses.
  let datadogRum: typeof DatadogRum;
  try {
    ({ datadogRum } = await import("@datadog/browser-rum"));
  } catch {
    initialized = false;
    return;
  }
  rumHandle = datadogRum;

  datadogRum.init({
    applicationId,
    clientToken,
    site: (import.meta.env.VITE_DATADOG_SITE as string | undefined) ?? "datadoghq.com",
    service: CITY.rumService,
    // Stamped by scripts/deploy.sh (git short SHA + env) so RUM sessions can be
    // filtered/compared by deploy. Falls back to "dev" for local/unset builds.
    version: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "dev",
    env:
      (import.meta.env.VITE_APP_ENV as string | undefined) ??
      (import.meta.env.MODE as string),
    sessionSampleRate: Number(
      (import.meta.env.VITE_DATADOG_SAMPLE_RATE as string | undefined) ?? 100
    ),
    sessionReplaySampleRate: 20,
    trackSessionAcrossSubdomains: true,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: "mask-user-input",
    allowedTracingUrls: [
      {
        match: (url: string) =>
          url.startsWith(window.location.origin + "/api"),
        propagatorTypes: ["tracecontext"],
      },
    ],
  });
}

/**
 * Record an explicit RUM custom action. Safe to call when RUM is not
 * configured (dev), or before initRum() has resolved — this no-ops rather
 * than statically importing the SDK, which would defeat the code-split.
 */
export function trackAction(name: string, ctx?: object): void {
  rumHandle?.addAction(name, ctx);
}
